import { Client, ClientSearchFilters, PatientModel, ReferringDoctor, Roles } from '@nrwl/rx-models';
import { HttpClient, HttpParams } from '@angular/common/http';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { StickyNote } from '@nrwl/rx-models';
import { environment } from '@nrwl/environment';
import format from 'date-fns/format';
import { DiagnosisTreatmentsState } from '@nrwl/rx-state-diagnosis-treatments';
import { select, Store } from '@ngrx/store';
import { UserStoreSelector } from '@nrwl/rx-state-user';
import { ClientLibraryFilterParamsUtils } from './client-library-filter-params.utils';
import { ClientLibraryQueryUtils } from './client-library-query.utils';
import { ClientLibraryQueryState } from '../../../../../models/client-library/client-library-query.interface';
import { FormRequestOptions, withFormType } from '../form-query/form-query-params.utils';

@Injectable({
  providedIn: 'root',
})
export class PatientsService {
  constructor(
    private http: HttpClient,
    private store: Store<DiagnosisTreatmentsState.State>,
  ) {
    this.store.pipe(select(UserStoreSelector.isProviderActive)).subscribe((value) => {
      this.isProviderActive = !!value;
    });
  }

  public isProviderActive = false;

  /**
   * Get all Patients by filters
   */
  getPatients(patient?: PatientModel): Observable<Array<PatientModel>> {
    const patientSearchUrl = `${environment.baseAPIUrlv3}patient/${this.buildQueryParms(patient)}`;
    return this.http.get<any>(patientSearchUrl).pipe(map((res) => res.results));
  }

  /**
   * Get all Patients by filters
   */
  savePatient(patient): Observable<any> {
    const patientSaveUrl = `${environment.baseAPIUrlv3}patient/`;
    return this.http.post(patientSaveUrl, patient);
  }

  private buildQueryParms(patient: PatientModel): string {
    let queryParam = '?all=false';
    if (patient) {
      if (patient.last_name) {
        queryParam = queryParam + ` &last_name__iexact=${patient.last_name}`;
      }
      if (patient.birth_date) {
        queryParam = queryParam + `&birth_date=${this.formatDate(patient.birth_date)}`;
      }
    }
    return queryParam;
  }

  findExistedUsers(query: {
    first_name: string;
    last_name: string;
    birth_date: string;
  }): Observable<any> {
    const params = new HttpParams()
      .set('first_name__iexact', query.first_name)
      .set('last_name__iexact', query.last_name)
      .set('birth_date', query.birth_date)
      .set('all', 'true');
    return this.http.get<any>(environment.baseAPIUrlv3 + `patient/`, {
      params,
    });
  }

  getPatientsFilter(
    query: ClientSearchFilters,
    emr = false,
    searchSource?: any,
    forceRxSearch = false,
  ): Observable<any> {
    // RWE-3091: remove for_initial: '0'
    const diagnoses_and__in = (query.existingParams as any).diagnoses_and__in;
    const treatments_and__in = (query.existingParams as any).treatments_and__in;
    const locations = (query.existingParams as any).locations;
    const form_status = (query.existingParams as any).form_status;
    const form_title = (query.existingParams as any).form_title;
    const birth_date__gte = (query.existingParams as any).birth_date__gte;
    const birth_date__lte = (query.existingParams as any).birth_date__lte;
    const surgeon = (query.existingParams as any).surgeon;

    const existingParams = { ...query.existingParams };
    if (diagnoses_and__in) {
      existingParams['diagnoses_and__in'] = Array.isArray(diagnoses_and__in)
        ? diagnoses_and__in.join(',')
        : diagnoses_and__in;
      existingParams['diagnoses_and__in'] =
        existingParams['diagnoses_and__in'] === '0'
          ? 'no_tags'
          : existingParams['diagnoses_and__in'];
    }
    if (treatments_and__in) {
      existingParams['treatments_and__in'] = Array.isArray(treatments_and__in)
        ? treatments_and__in.join(',')
        : treatments_and__in;
      existingParams['treatments_and__in'] =
        existingParams['treatments_and__in'] === '0'
          ? 'no_tags'
          : existingParams['treatments_and__in'];
    }
    if (locations) {
      delete existingParams['locations'];
      if (locations.indexOf('all') === -1) {
        existingParams['locations__in'] = Array.isArray(locations)
          ? locations.join(',')
          : locations;
      }
    }
    if (form_status) {
      existingParams['form_status'] = Array.isArray(form_status)
        ? form_status.join(',')
        : form_status;
    }
    // if (form_title) {
    //     existingParams['form_title'] = Array.isArray(form_title) ? form_title.join(',') : form_title;
    // }

    if (birth_date__gte) {
      existingParams['birth_date__gte'] = this.getDateByAge(birth_date__gte);
    }
    if (birth_date__lte) {
      existingParams['birth_date__lte'] = this.getDateByAge(birth_date__lte);
    }

    if (birth_date__gte === birth_date__lte && birth_date__gte) {
      existingParams['birth_date__lte'] = this.getDateByAge(+birth_date__gte - 1);
    }

    if (surgeon && this.isProviderActive) {
      delete existingParams['surgeon'];
      existingParams['patient_surgeon'] =
        surgeon[0] === 'no_surgeons' ? 'no_patient_surgeons' : surgeon.join(',');
    }

    const params = Object.assign(existingParams, {
      page_size: '30',
      expand: 'doctor,referring_doctor,location',
      fields:
        'id,doctor.first_name,doctor.last_name,referring_doctor.first_name,referring_doctor.last_name,location.id,location.name,diagnoses,treatments,first_name,middle_name,last_name,phone_number,email,avatar,is_deleted,signed,birth_date,last_visit,remote_id,mrn,amazing_transformation',
    });

    let querystring = new HttpParams({ fromObject: params }).toString();

    // RXADMIN-3040: Once PN integration is done, use this endpoint for Patient Search
    //               Check baseBEUrl, it might require to change it to api.staging.rxphoto.com

    if (forceRxSearch && params['search']) {
      return this.http.get<any>(environment.baseAPIUrlv3 + `patient/?` + querystring);
    }
    if (emr && +searchSource === 0 && params['search']) {
      return this.http.get<any>(
        `${environment.integrationBaseAPIUrlV3}search-patients/?search=${params['search']}`,
      );
    } else if (+searchSource === 2 && params['search']) {
      // Envision
      return this.http.get<any>(
        this.buildSearchUrl(params['search'], ['LastName', 'FirstName', 'MobilePhone']),
      );
    } else if (+searchSource === 3 && params['search']) {
      return this.http.get<any>(
        `${environment.integrationBaseAPIUrlV3}search-patients/?search=${params['search']}`,
      );
    } else if (+searchSource === 4 && params['search']) {
      return this.http.get<any>(
        `${environment.integrationBaseAPIUrlV3}search-patients/?search=${params['search']}`,
      );
    } else if (+searchSource === 5 && params['search']) {
      // Nextech
      return this.http.get<any>(
        this.buildSearchUrl(params['search'], ['last_name', 'first_name', 'patient_number']),
      );
    } else if (+searchSource === 6 && params['search']) {
      // Streamline
      return this.http.get<any>(this.buildSearchUrl(params['search'], ['last_name', 'first_name']));
    } else if (+searchSource === 7 && params['search']) {
      // Athena
      return this.http.get<any>(this.buildSearchUrl(params['search'], ['last_name', 'first_name']));
    } else if (+searchSource === 8 && params['search']) {
      // Modmed
      return this.http.get<any>(
        this.buildSearchUrl(params['search'], ['family', 'given', 'identifier']),
      );
    } else if (+searchSource === 9 && params['search']) {
      // Drchrono
      return this.http.get<any>(
        this.buildSearchUrl(params['search'], ['last_name', 'first_name', 'patient_number']),
      );
    } else {
      if (emr && +searchSource === 0 && params['search']) {
        delete params['search'];
        querystring = new HttpParams({ fromObject: params }).toString();
      }
      querystring = querystring.replace(/form_title/g, 'form_title[]');
      querystring = querystring.replace(/implant_volumes/g, 'implant_volume');
      querystring = querystring.replace(/implant_planes/g, 'implant_plane');
      return this.http.get<any>(environment.baseAPIUrlv3 + `patient/?` + querystring);
    }
  }

  private buildSearchUrl(queryString: string, keys: string[]): string {
    const params = this.parseQueryString(queryString, keys);
    const url = new URL(`${environment.integrationBaseAPIUrlV3}search-patients/`);

    Object.keys(params).forEach((key) => {
      url.searchParams.append(key, params[key]);
    });

    return url.toString();
  }

  private parseQueryString(queryString: string, keys: string[]): { [key: string]: string } {
    const parts = queryString.split(',').map((part) => part.trim());

    const params: { [key: string]: string } = {};
    let keyIndex = 0;

    if (/\d/.test(parts[0]) && keys.length >= 3) {
      params[keys[2]] = parts[0];
      return params;
    }

    parts.forEach((part) => {
      if (part.length > 0 && keyIndex < keys.length) {
        params[keys[keyIndex]] = part.toLowerCase();
      }
      keyIndex++;
    });

    return params;
  }

  getById(id: number, byRemoteId = false, includeArchived = false): Observable<any> {
    let params = new HttpParams();

    if (byRemoteId) {
      params = params.set('by_remote_id', 1);
    }

    if (includeArchived) {
      params = params.set('all', true);
    }

    return this.http.get<any>(environment.baseAPIUrlv3 + `patient/${id}/`, { params });
  }

  pullOrSyncEmr(remote_id): Observable<any> {
    return this.http.post<any>(environment.integrationBaseAPIUrlV3 + 'pull-or-sync/', {
      remote_id: remote_id,
    });
  }

  getMorePatients(next: string): Observable<any> {
    return this.http.get(next);
  }

  getRefferingDoctors(): Observable<ReferringDoctor[]> {
    const url = environment.baseAPIUrlv3 + 'referring-doctor/';
    return this.http
      .get(url)
      .pipe(map((res: any) => res.map((doctor: any) => new ReferringDoctor(doctor))));
  }

  public getStickyNoteById(clientId: number): Observable<StickyNote[]> {
    const url = `${environment.baseAPIUrlv3}sticky-note/?patient=${clientId}`;
    return this.http.get<StickyNote[]>(url).pipe(filter((res) => !!res.length));
  }

  public postStickyNoteById(data: { patient: number; note: string }): Observable<StickyNote> {
    const url = `${environment.baseAPIUrlv3}sticky-note/`;
    return this.http.post<any>(url, data).pipe(
      map((item) => {
        item.author = item.author.id;
        return item;
      }),
    );
  }

  public patchStickyNoteById(stickyNoteId: number, data: { note: string }): Observable<StickyNote> {
    const url = `${environment.baseAPIUrlv3}sticky-note/${stickyNoteId}/`;
    return this.http.patch<any>(url, data).pipe(
      map((item) => {
        item.author = item.author.id;
        return item;
      }),
    );
  }

  createClient(data: any): Observable<Client> {
    const url = environment.baseAPIUrlv3 + 'patient/';
    const dataLocation = data.location?.length ? data.location.map((item) => item.id) : undefined;
    const formData = data.avatar ? data.avatar : new FormData();
    delete data.avatar;

    Object.keys(data).forEach((item) =>
      data[item] ? (item === 'location' ? false : formData.append(item, data[item])) : false,
    );
    if (dataLocation?.length) {
      dataLocation.forEach((loc) => {
        formData.append('location', loc);
      });
    }
    return this.http.post(url, formData).pipe(map((res: any) => new Client(res)));
  }

  updatePatient(patientId: number, data: any) {
    return this.http
      .patch(`${environment.baseAPIUrlv3}patient/${patientId}/`, data)
      .pipe(map((res: any) => new Client(res)));
  }

  archiveClient(clientId: number): Observable<any> {
    const url = `${environment.baseAPIUrlv3}patient/${clientId}/`;
    return this.http.delete(url);
  }

  restoreClient(clientId: number): Observable<any> {
    const url = `${environment.baseAPIUrlv3}patient/${clientId}/restore/`;
    return this.http.patch(url, { is_deleted: false });
  }

  mergeClient(payload: { target: number; sources: number[] }): Observable<any> {
    const url = `${environment.baseAPIUrlv3}patient/${payload.target}/merge/?async=1`;
    return this.http.post(url, { sources: payload.sources });
  }

  updateClient(data: Client): Observable<any> {
    const url = `${environment.baseAPIUrlv3}patient/${data.id}/`;
    return this.http.patch(url, data);
  }

  setPassword(data: { password: string; patientId: number }): Observable<Client> {
    const url = environment.baseAPIUrlv3 + 'patient/' + data.patientId + '/set-password/';
    return this.http
      .post(url, { password: data.password })
      .pipe(map((res: any) => new Client(res)));
  }

  public getFormsCount(clientId: number, options: FormRequestOptions = {}): Observable<number> {
    const params = withFormType(
      new HttpParams().set('fields', 'count').set('is_deleted', 'false').set('limit', '100000'),
      options,
    );

    return this.http
      .get<any>(`${environment.baseAPIUrlv3}patient/${clientId}/dynamic-forms/`, { params })
      .pipe(map((res) => res.count));
  }

  public getPatientForms(
    patientId: number,
    query: ClientLibraryQueryState,
    options: FormRequestOptions = {},
  ): Observable<any> {
    const includePersonalizedPlanFields = options.formType === 'all';
    const expand = includePersonalizedPlanFields
      ? 'author,template.category,assigned_provider'
      : 'author,template.category';
    const fields = [
      'id',
      'template.category.is_pne',
      'template.category.name',
      'template.category.id',
      'template.type',
      ...(includePersonalizedPlanFields ? ['assigned_provider.id'] : []),
      'title',
      'publication_date',
      'status',
      'updated_at',
      'shared_with_patient',
      'author.id',
      'author.first_name',
      'author.last_name',
      'patient',
      'is_deleted',
      'template',
      'previous_version',
      'version_number',
      'version_display_date',
      'version_actor_name',
      'version_actor_action',
    ].join(',');

    const params = ClientLibraryQueryUtils.buildQueryParams(
      withFormType(
        new HttpParams().set('is_deleted', 'false').set('expand', expand).set('fields', fields),
        options,
      ),
      query,
    );

    return this.http
      .get<any>(`${environment.baseAPIUrlv3}patient/${patientId}/dynamic-forms/`, {
        params,
        observe: 'response',
      })
      .pipe(map((res: any) => res.body));
  }

  getArchivedPatientForms(clientId: number, options: FormRequestOptions = {}): Observable<any> {
    const params = withFormType(
      new HttpParams()
        .set('is_deleted', 'true')
        .set('limit', '100000')
        .set('expand', 'template')
        .set(
          'fields',
          'id,title,publication_date,status,updated_at,shared_with_patient,author,patient,is_deleted,template,template.type,previous_version,version_display_date,version_actor_name,version_actor_action',
        ),
      options,
    );

    return this.http
      .get<any>(`${environment.baseAPIUrlv3}patient/${clientId}/dynamic-forms/`, { params })
      .pipe(map((res: any) => res.results));
  }

  public getClientLibraryFilterValues(
    patientId: number,
    field: string,
    pageSize: number,
    offset: number,
    options: FormRequestOptions = {},
  ): Observable<any> {
    const params = withFormType(
      ClientLibraryFilterParamsUtils.getClientLibraryFilterParams(
        field,
        new HttpParams()
          .set('patient', patientId)
          .set('limit', pageSize)
          .set('offset', offset)
          .set('is_deleted', 'false'),
      ),
      options,
    );

    return this.http.get<any>(`${environment.baseAPIUrlv3}form/`, {
      params,
      observe: 'response',
    });
  }

  public getSurgeons(
    pageSize: number,
    offset: number,
    search = '',
    options: { assignment?: 'forms' | 'plans' } = {},
  ): Observable<any> {
    let params = new HttpParams()
      .set('limit', pageSize)
      .set('offset', offset)
      .set('is_active', 'true')
      .set('ordering', 'last_name,first_name')
      .set('fields', 'last_name,first_name,id,photo,role,is_active');

    if (options.assignment) {
      params = params.set(
        options.assignment === 'plans' ? 'has_plans_assigned' : 'has_forms_assigned',
        'true',
      );
    }

    if (search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.store.select(UserStoreSelector.getUser).pipe(
      filter((user) => !!user),
      take(1),
      switchMap((user) => {
        const endpoint = Number(user?.role) === Roles.Employee ? 'surgeon' : 'doctor';
        return this.http.get<any>(`${environment.baseAPIUrlv3}${endpoint}/`, {
          params,
          observe: 'response',
        });
      }),
    );
  }

  getByIdv3(id: number): Observable<any> {
    return this.http
      .get(environment.baseAPIUrlv3 + `patient/${id}/`)
      .pipe(map((res: any) => new Client(res)));
  }

  getPatientLegacyForms(clientId: number): Observable<any> {
    return this.http
      .get(environment.baseAPIUrlv3 + `patient/${clientId}/forms/?limit=1000`)
      .pipe(map((res: any) => res.results));
  }

  getPatientLegasyForm(id: number) {
    return this.http.get(environment.baseAPIUrlv3 + `patientform/${id}/`);
  }

  formatDate(date: Date | string): string {
    try {
      return format(new Date(date), 'MM/dd/yyyy');
    } catch (e) {
      return '';
    }
  }

  getDateByAge(age: number): string {
    const date = new Date();
    date.setFullYear(date.getFullYear() - +age - 1);
    return format(new Date(date), 'yyyy-MM-dd');
  }

  getAvatarColor(str: string): string {
    str = str || Math.random().toString(36).substring(2) + Date.now();

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 6) - hash);
    }
    let colour = '#';
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      colour += ('00' + value.toString(16)).substr(-2);
    }
    return colour;
  }

  getCurrentPatient() {
    return this.http.get(environment.baseAPIUrlv3 + `patient/me/ `);
  }
}
